import React from 'react';
import { useDispatch } from 'react-redux';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormHelperText,
  Grid,
  TextField,
  FormControlLabel,
  InputLabel,
  Checkbox,
  Select,
  MenuItem,
  Typography
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { updateProfile } from 'src/actions/accountActions';
import { USER_ROLES } from 'src/constants';


const useStyles = makeStyles(() => ({
  root: {}
}));

function GeneralSettings({ user, className, ...rest }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  return (
    <Formik
      enableReinitialize
      initialValues={{
        firstName: user.firstName,
        lastName: user.lastName,
        company: user.company,
        cellphone: user.cellphone,
        email: user.email,
        phone: user.phone,
        address: user.address,
        zipcode: user.zipcode,
        city: user.city,
        usertype: user.usertype || USER_ROLES.PROPERTYOWNERADMIN,
        sendStatus: user.sendStatus || false,
        sendPeriod: user.sendPeriod || 0,
        sendDailyTime: user.sendDailyTime || new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        sendMonday: user.sendMonday || true,
        sendTuesday: user.sendTuesday || true,
        sendWednesday: user.sendWednesday || true,
        sendThursday: user.sendThursday || true,
        sendFriday: user.sendFriday || true,
        sendSaturday: user.sendSaturday || true,
        sendSunday: user.sendSunday || true,
        sendMonthlyDay: user.sendMonthlyDay || 1, 
      }}
      validationSchema={Yup.object().shape({
        firstName: Yup.string().max(255).required('Vänligen ange ditt förnamn'),
        lastName: Yup.string().max(255).required('Vänligen ange ditt efternamn'),
        company: Yup.string().max(255).required('Vänligen ange ditt företagsnamn'),
        email: Yup.string().email('Vänligen ange en giltig e-post adress').max(255).required('Vänligen ange din e-post adress'),
        cellphone: Yup.string().max(255).required('Vänligen ange ditt mobiltelefonummer')
      })}
      onSubmit={async (values, {
        resetForm,
        setErrors,
        setStatus,
        setSubmitting
      }) => {
        try {
          await dispatch(updateProfile(values));
          resetForm();
          setStatus({ success: true });
          enqueueSnackbar('Användarprofilen är uppdaterad', {
            variant: 'success'
          });
        } catch (error) {
          setStatus({ success: false });
          setErrors({ submit: error.message });
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values
      }) => (
        <form onSubmit={handleSubmit}>
          <Card
            className={clsx(classes.root, className)}
            {...rest}
          >
            <CardHeader title="Användarprofil" />
            <Divider />
            <CardContent>
              <Grid
                container
                spacing={4}
              >
                <Grid
                  item
                  md={6}
                  xs={12}
                >
                  <TextField
                    error={Boolean(touched.firstName && errors.firstName)}
                    fullWidth
                    helperText={touched.firstName && errors.firstName}
                    label="Förnamn"
                    name="firstName"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    required
                    type="firstName"
                    value={values.firstName}
                    variant="outlined"
                  />
                </Grid>
                <Grid
                  item
                  md={6}
                  xs={12}
                >
                  <TextField
                    error={Boolean(touched.lastName && errors.lastName)}
                    fullWidth
                    helperText={touched.lastName && errors.lastName}
                    label="Efternamn"
                    name="lastName"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    required
                    type="lastName"
                    value={values.lastName}
                    variant="outlined"
                  />
                </Grid>
                <Grid
                  item
                  md={6}
                  xs={12}
                >
                  <TextField
                    error={Boolean(touched.company && errors.company)}
                    fullWidth
                    helperText={touched.company && errors.company}
                    label="Företag"
                    name="company"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    required
                    type="company"
                    value={values.company}
                    variant="outlined"
                  />
                </Grid>
                <Grid
                  item
                  md={6}
                  xs={12}
                >
                  <TextField
                    error={Boolean(touched.cellphone && errors.cellphone)}
                    fullWidth
                    helperText={touched.cellphone && errors.cellphone ? errors.cellphone : 'Vi kommer att använda oss av telefonnumret när vi kontaktar dig'}
                    label="Mobiltelefon"
                    name="cellphone"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    required
                    value={values.cellphone}
                    variant="outlined"
                  />
                </Grid>

                <Grid
                  item
                  md={6}
                  xs={12}
                >
                  <TextField
                    error={Boolean(touched.email && errors.email)}
                    fullWidth
                    helperText={touched.email && errors.email ? errors.email : 'Vi kommer att använda oss av denna adress när vi kontaktar dig'}
                    label="E-post adress"
                    name="email"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    required
                    type="email"
                    value={values.email}
                    variant="outlined"
                  />
                </Grid>
                <Grid
                  item
                  md={6}
                  xs={12}
                >
                  <TextField
                    error={Boolean(touched.phone && errors.phone)}
                    fullWidth
                    helperText={touched.phone && errors.phone}
                    label="Telefon"
                    name="phone"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.phone}
                    variant="outlined"
                  />
                </Grid>


                <Grid
                  item
                  md={6}
                  xs={12}
                >
                  <TextField
                    error={Boolean(touched.address && errors.address)}
                    fullWidth
                    helperText={touched.address && errors.address}
                    label="Postadress"
                    name="address"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.address}
                    variant="outlined"
                  />
                </Grid>
                <Grid
                  item
                  md={6}
                  xs={12}
                >
                  <TextField
                    error={Boolean(touched.zipcode && errors.zipcode)}
                    fullWidth
                    helperText={touched.zipcode && errors.zipcode}
                    label="Postnummer"
                    name="zipcode"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.zipcode}
                    variant="outlined"
                  />
                </Grid>

                <Grid
                  item
                  md={12}
                  xs={12}
                >
                  <TextField
                    error={Boolean(touched.city && errors.city)}
                    fullWidth
                    helperText={touched.city && errors.city}
                    label="Ort"
                    name="city"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.city}
                    variant="outlined"
                  />
                </Grid>

                <Grid item md={12} xs={12}>
                {/*values.usertype === USER_ROLES.PROPERTYOWNERADMIN &&
                <Box mt={2}>
                  <FormControlLabel
                    label="Skicka sensorstatus"
                    control={
                      <Checkbox
                        name="sendStatus"
                        checked={values.sendStatus}
                        onChange={handleChange}
                        value={values.sendStatus}
                      />
                    }
                  />
                </Box>
                */}
                {/*values.sendStatus == true &&
                <>
                <Box>
                  <InputLabel id="sendPeriod-label" sx={{mb: 1}}>Period för rapportgenerering *</InputLabel>
                  <Select
                    labelId="sendPeriod-label"
                    name="sendPeriod"
                    value={values.sendPeriod}
                    onChange={handleChange}
                    className={classes.selectEmpty}
                    inputProps={{ 'aria-label': 'Without label' }}
                    sx={{mr: 1, mb: 1}}
                    error={values.sendPeriod === 0}
                    helperText={values.sendPeriod === 0 ? "Du måste välja period" : ""}
                  >
                    <MenuItem value={0}><em>Välj period</em></MenuItem>
                    <MenuItem value={1}>Dagligen</MenuItem>
                    <MenuItem value={2}>Veckovis</MenuItem>
                    <MenuItem value={3}>Månadsvis</MenuItem>
                  </Select>
                  {values.sendPeriod === 0 &&
                    <FormHelperText error={values.sendPeriod === 0}>Du måste välja en period</FormHelperText>
                  }
                  {values.sendPeriod === 1 && (
                    <TextField
                      label="Tidpunkt för daglig rapport"
                      name="sendDailytime"
                      type="time"
                      value={values.sendDailyTime}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        step: 300, // 5-minuterssteg
                      }}
                      sx={{width: "150px"}}
                    />
                  )}
                </Box>
                
                {values.sendPeriod === 2 && (
                  <Box>
                    <InputLabel id="weekdays-label" sx={{mb: 1}}>Välj veckodagar</InputLabel>
                    <Grid container spacing={1}>
                    <Grid item>
                    <Checkbox
                      checked={values.sendMonday}
                      onChange={handleChange}
                      name="sendMonday"
                    />
                    <Typography display="inline">Måndag</Typography>
                    </Grid>
                    <Grid item>
                    <Checkbox
                      checked={values.sendTuesday}
                      onChange={handleChange}
                      name="sendTuesday"
                    />
                    <Typography display="inline">Tisdag</Typography>
                    </Grid>
                    <Grid item>
                    <Checkbox
                      checked={values.sendWednesday}
                      onChange={handleChange}
                      name="sendWednesday"
                    />
                    <Typography display="inline">Onsdag</Typography>
                    </Grid>
                    <Grid item>
                    <Checkbox
                      checked={values.sendThursday}
                      onChange={handleChange}
                      name="sendThursday"
                    />
                    <Typography display="inline">Torsdag</Typography>
                    </Grid>
                    <Grid item>
                    <Checkbox
                      checked={values.sendFriday}
                      onChange={handleChange}
                      name="sendFriday"
                    />
                    <Typography display="inline">Fredag</Typography>
                    </Grid>
                    <Grid item>
                    <Checkbox
                      checked={values.sendSaturday}
                      onChange={handleChange}
                      name="sendSaturday"
                    />
                    <Typography display="inline">Lördag</Typography>
                    </Grid>
                    <Grid item>
                    <Checkbox
                      checked={values.sendSunday}
                      onChange={handleChange}
                      name="sendSunday"
                    />
                    <Typography display="inline">Söndag</Typography>
                    </Grid>
                    </Grid>
                  </Box>
                )}

                {values.sendPeriod === 3 && (
                  <Box>
                    <InputLabel sx={{mb: 1}}>Välj dag i månaden</InputLabel>
                    <Select
                      name="sendMonthlyDay"
                      value={values.sendMonthlyDay || ''}
                      onChange={handleChange}
                    >
                      {[...Array(31).keys()].map((day) => (
                        <MenuItem key={day} value={day + 1}>
                          {day + 1}
                        </MenuItem>
                      ))}
                      <MenuItem value="32">Sista dagen i månaden</MenuItem>
                    </Select>
                  </Box>
                )}
                </>
                */}
                </Grid>

              </Grid>
              {errors.submit && (
                <Box mt={3}>
                  <FormHelperText error>
                    {errors.submit}
                  </FormHelperText>
                </Box>
              )}
            </CardContent>
            <Divider />
            <Box
              p={2}
              display="flex"
              justifyContent="flex-end"
            >
              <Button
                color="secondary"
                disabled={isSubmitting}
                type="submit"
                variant="contained"
              >
                Spara
              </Button>
            </Box>
          </Card>
        </form>
      )}
    </Formik>
  );
}

GeneralSettings.propTypes = {
  className: PropTypes.string,
  user: PropTypes.object.isRequired
};

export default GeneralSettings;
